import type { ReactElement } from 'react';
import type { TooltipProps } from 'recharts';
import type { SurveyGroupCompareChartRow } from '../survey-group-compare.helpers';

type TickPayload = {
  value?: string | number;
};

export type SurveyGroupCompareAnswerAxisTickProps = {
  x: number;
  y: number;
  payload: TickPayload;
  index: number;
  rows: SurveyGroupCompareChartRow[];
};

/**
 * Recharts X-axis tick: optional answer thumbnail above the label (compare grouped bar chart).
 */
export function SurveyGroupCompareAnswerAxisTick({
  x,
  y,
  payload,
  index,
  rows,
}: SurveyGroupCompareAnswerAxisTickProps): ReactElement {
  const row = rows[index];
  const url = row?.answerImageUrl;
  const raw = payload?.value;
  const label = raw === undefined || raw === null ? '' : String(raw);
  const shortLabel = label.length > 26 ? `${label.slice(0, 24)}…` : label;

  return (
    <g transform={`translate(${x},${y})`}>
      {url ? (
        <foreignObject
          x={-22}
          y={-46}
          width={44}
          height={40}
        >
          <div className="flex h-10 w-11 justify-center">
            <img
              src={url}
              alt=""
              className="h-9 w-9 rounded-md border border-gray-200 object-cover bg-gray-50"
            />
          </div>
        </foreignObject>
      ) : null}
      <text
        x={0}
        y={url ? 10 : 4}
        textAnchor="middle"
        fill="#6b7280"
        fontSize={11}
      >
        {shortLabel}
      </text>
    </g>
  );
}

export function SurveyGroupCompareTooltip({
  active,
  payload,
  label,
}: TooltipProps<number, string>): ReactElement | null {
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload as SurveyGroupCompareChartRow | undefined;
  const img = row?.answerImageUrl;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-md">
      {img ? (
        <div className="mb-2 flex justify-center">
          <img
            src={img}
            alt=""
            className="mx-auto h-16 w-16 rounded-md border border-gray-200 object-cover"
          />
        </div>
      ) : null}
      <p className="mb-1 font-medium text-gray-900">
        {label}
      </p>
      <ul className="space-y-0.5">
        {payload.map((p) => (
          <li
            key={String(p.dataKey)}
            className="flex justify-between gap-4 text-gray-700"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: p.color }}
              />
              <span className="truncate">
                {p.name}
              </span>
            </span>
            <span className="shrink-0">
              {typeof p.value === 'number' ? `${p.value.toFixed(1)}%` : p.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
